import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Lock, Plus, Repeat, Search } from 'lucide-react'
import {
  DEPARTAMENTE,
  departamentulRolului,
  normalizeaza,
  numeDepartament,
  type DepartamentSlug,
} from '@b4l/shared'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { Button } from '../../components/ui/Button'
import { StareLista } from '../../components/ui/StareLista'
import { Tablou } from '../../components/ui/Tablou'
import { useConfirmare } from '../../components/ui/Confirmare'
import { Avatare, CardFata } from './CardFata'
import {
  COLOANA_GATA,
  COLOANE,
  MESAJ_ARHIVARE,
  MESAJ_STERGERE,
  CULOARE_DEPARTAMENT,
  PAS_URMATOR,
  actiuneSpre,
  coloanaPeTablou,
  esteFinanciar,
  ordoneazaPeTablou,
  tinteCard,
  type StareTablou,
  FEREASTRA_GATA,
} from './constante'
import {
  depasit,
  esteAlMeu,
  mesajEroare,
  useActiune,
  useCautareTablou,
  useReimprospateaza,
  useCreeaza,
  useDepartamente,
  useTablou,
  ziScurt,
  type CardTask,
  type Scop,
  type SumarDepartament,
} from './date'
import { FormularTask } from './Formulare'
import { AlegeOameni } from './AlegeOameni'
import { VedereCalendar } from './Calendar'
import { PanouTask } from './PanouTask'
import { VedereActivitate, VedereAnsamblu, VedereArhiva } from './Vederi'
import { ButonOrdine, type Ordine } from '../../components/ui/ButonOrdine'
import { useDebounced } from '../../hooks/useDebounced'

/**
 * Task-uri pe departamente (Madalin, 12.10.2026; macheta aprobata v5:
 * https://claude.ai/artifact/SNQjaFajtX2GCawwJvGZnF; planul: docs/superpowers/plans/2026-10-12-taskuri-pe-departamente-plan.md).
 *
 * Munca departamentului, de la ce a intrat pana la ce s-a terminat. Task-ul e al departamentului;
 * un om il PREIA, nu i se atribuie. Fiecare vede departamentul lui si „Trimise de noi" (cererile
 * catre altii); adminii vad tot, plus „Ansamblu".
 *
 * „Ale mele" (Madalin, 19.10.2026; macheta: https://claude.ai/artifact/E5onnbcP1QwbbeUVdDXKYG):
 * ce are omul pe el, din orice departament, plus privatele date de el altora — cu Tablou si
 * Calendar. Cifrele lui nu le vede nimeni altcineva; fara email, fara alerte.
 *
 * Adresa tine ce merita trimis cuiva: `?dep=` (departamentul), `?vedere=` si `?view=<id>`
 * (task-ul deschis in panou — linkurile din alte module, notificarile si monitorul contabilului
 * duc aici).
 */

type Vedere = 'tablou' | 'calendar' | 'activitate' | 'arhiva' | 'ansamblu'
type Filtru = 'toate' | 'urgente' | 'depasite' | 'primite' | 'ale-mele' | 'private' | 'email'

const SCOPURI = new Set<string>([
  ...DEPARTAMENTE.map((d) => d.slug),
  'trimise',
  'toate',
  'ale-mele',
])
const VEDERI = new Set<string>(['tablou', 'calendar', 'activitate', 'arhiva', 'ansamblu'])
const CHEIE_ORDINE = 'b4l.taskuri.ordini'

/** Coloanele din „Ale mele": fara „De preluat", cu textele spuse omului, nu departamentului. */
const COLOANE_ALE_MELE = COLOANE.filter((c) => c.id !== 'de_preluat').map((c) =>
  c.id === 'in_progress'
    ? {
        ...c,
        ajutor:
          'Ce ai tu de făcut acum, din orice departament — preluat de tine, dat ție sau creat pentru tine.',
        gol: 'Nimic pe tine acum. Scrie mai sus un task privat, sau preia unul de la departament.',
      }
    : c.id === 'in_asteptare'
      ? { ...c, gol: 'Nu aștepți pe nimeni.' }
      : c,
)

/** Ordinea fiecarei coloane, tinuta minte pe browser. Implicit: in coloane cele mai vechi sus
 *  (ce asteapta de mult nu se pierde), in „Finalizate" cele terminate cel mai recent. */
type Ordini = Partial<Record<StareTablou, Ordine>>
const ORDINE_IMPLICITA: Ordini = { done: 'noi' }

function citesteOrdini(): Ordini {
  try {
    const v = JSON.parse(localStorage.getItem(CHEIE_ORDINE) ?? '{}') as unknown
    return v && typeof v === 'object' ? { ...ORDINE_IMPLICITA, ...(v as Ordini) } : ORDINE_IMPLICITA
  } catch {
    return ORDINE_IMPLICITA
  }
}

export default function TaskuriPage() {
  const eu = useAuthStore((s) => s.user)
  const poateScrie = useAuthStore((s) => s.permissions.includes('tasks:write'))
  const admin = eu?.role === 'admin'
  const alMeu = eu ? departamentulRolului(eu.role) : null

  const [param, setParam] = useSearchParams()
  const scopUrl = param.get('dep')
  // Fara departament (un rol nou), „Ale mele" — nu un ecran gol.
  const scop: Scop =
    scopUrl && SCOPURI.has(scopUrl) ? (scopUrl as Scop) : admin ? 'toate' : (alMeu ?? 'ale-mele')
  const aleMele = scop === 'ale-mele'
  const vedereUrl = param.get('vedere') ?? 'tablou'
  // Calendarul e al lui „Ale mele"; Ansamblul, al adminilor, pe departamente.
  const vedere: Vedere =
    VEDERI.has(vedereUrl) &&
    (vedereUrl !== 'ansamblu' || (admin && !aleMele)) &&
    (vedereUrl !== 'calendar' || aleMele)
      ? (vedereUrl as Vedere)
      : 'tablou'
  const deschisId = Number(param.get('view')) || null

  const schimba = (modificari: Record<string, string | null>) =>
    setParam(
      (p) => {
        const n = new URLSearchParams(p)
        for (const [k, v] of Object.entries(modificari)) {
          if (v == null) n.delete(k)
          else n.set(k, v)
        }
        return n
      },
      // Deschiderea si inchiderea panoului nu umplu istoricul browserului.
      { replace: Object.keys(modificari).every((k) => k === 'view') },
    )

  const [filtru, setFiltru] = useState<Filtru>('toate')
  const [cauta, setCauta] = useState('')
  const [ordini, setOrdini] = useState<Ordini>(citesteOrdini)
  const ordineaLui = (c: StareTablou): Ordine => ordini[c] ?? 'vechi'
  const schimbaOrdinea = (c: StareTablou) => (o: Ordine) => setOrdini((v) => ({ ...v, [c]: o }))
  const [nou, setNou] = useState(false)
  const [deDat, setDeDat] = useState<CardTask | null>(null)
  const campCauta = useRef<HTMLInputElement>(null)

  const departamente = useDepartamente()
  const tablou = useTablou(scop, vedere === 'tablou' || vedere === 'calendar')
  /*
    Cautarea (04.11.2026): pe server, in titlu, detalii, oameni, numar si comentarii — doar printre
    cardurile tabloului. Se improspateaza odata cu tabloul (`dataUpdatedAt`).
  */
  const textCautat = useDebounced(cauta.trim(), 250)
  const cautare = useCautareTablou(
    scop,
    vedere === 'tablou' || vedere === 'calendar' ? textCautat : '',
    tablou.dataUpdatedAt,
  )
  const potriviri = useMemo(
    () =>
      /*
        Rezultatul pentru alt text sau alt departament nu filtreaza: pana vine cel nou, filtrul
        local. Dar la reimprospatarea tabloului (alta `versiune`, acelasi text) ramane cel vechi —
        altfel motivele ar clipi la fiecare minut si dupa fiecare mutare.
      */
      cauta.trim() &&
      cautare.data &&
      cautare.data.pentru.q === textCautat &&
      cautare.data.pentru.scop === scop
        ? new Map(cautare.data.potriviri.map((p) => [p.id, p.motive]))
        : null,
    [cauta, cautare.data, textCautat, scop],
  )
  const actiune = useActiune()
  const { cere, confirma } = useConfirmare()
  const reimprospateaza = useReimprospateaza()
  /*
    De pe card (Madalin, 16.10.2026): „Arhivează" pentru oricine are voie să mute task-ul — rămâne
    în istoric —, „Șterge" doar pentru admini. Aceleași ferestre ca în panoul task-ului.
  */
  const stergeTask = useMutation({
    mutationFn: async (id: number) => api.delete(`/tasks/${id}`),
    onSuccess: () => {
      toast.success('Task șters')
      reimprospateaza()
    },
    onError: (e) => toast.error(mesajEroare(e, 'Nu s-a putut șterge task-ul.')),
  })
  const arhiveaza = async (t: CardTask) => {
    const motiv = await cere({
      titlu: `Arhivezi „${t.titlu}”?`,
      mesaj: MESAJ_ARHIVARE,
      eticheta: 'Motiv (opțional)',
      buton: 'Arhivează',
    })
    if (motiv === null) return
    actiune.mutate(
      { id: t.id, actiune: 'arhiveaza', motiv: motiv || null },
      { onSuccess: () => toast.success('Task arhivat — îl găsești în Arhivă, la „Arhivate”.') },
    )
  }
  const sterge = async (t: CardTask) => {
    const da = await confirma({
      titlu: `Ștergi definitiv „${t.titlu}”?`,
      mesaj: MESAJ_STERGERE,
      buton: 'Șterge definitiv',
      pericol: true,
    })
    if (da) stergeTask.mutate(t.id)
  }

  // Cati oameni are departamentul — pentru avertismentul blând de la „În lucru". Numarat exact,
  // din utilizatorii activi si rolul lor (departamentul se deduce din rol).
  const colegi = useQuery<{ users: { id: number; role: string }[] }>({
    queryKey: ['taskuri', 'oameni'],
    queryFn: async () => (await api.get('/tasks/mentionable-users')).data,
    staleTime: 10 * 60_000,
  })
  const oameniInDep =
    scop && scop !== 'toate' && scop !== 'trimise'
      ? (colegi.data?.users ?? []).filter((u) => departamentulRolului(u.role) === scop).length
      : 0

  useEffect(() => {
    try {
      localStorage.setItem(CHEIE_ORDINE, JSON.stringify(ordini))
    } catch {
      /* fara stocare: ordinea ramane doar pana la reincarcare */
    }
  }, [ordini])

  // La schimbarea departamentului, filtrul se goleste — altfel „Preluate de mine" ramane aplicat
  // pe „Trimise de noi", unde n-are sens, si tabloul pare gol.
  useEffect(() => setFiltru('toate'), [scop])

  // Scurtaturi: N = task nou, / = cauta. Nu cand scrii intr-un camp sau e deschisa o fereastra.
  useEffect(() => {
    const tasta = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement
      if (/INPUT|TEXTAREA|SELECT/.test(t.tagName) || t.isContentEditable) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (deschisId || nou || document.querySelector('[data-modal]')) return
      if (e.key.toLowerCase() === 'n' && poateScrie) {
        e.preventDefault()
        setNou(true)
      } else if (e.key === '/' && vedere === 'tablou') {
        e.preventDefault()
        campCauta.current?.focus()
      }
    }
    document.addEventListener('keydown', tasta)
    return () => document.removeEventListener('keydown', tasta)
  }, [deschisId, nou, poateScrie, vedere])

  const toate = useMemo(() => tablou.data?.taskuri ?? [], [tablou.data])
  const trimise = scop === 'trimise'
  const cuDepartament = scop === 'toate' || trimise

  /*
    In „Ale mele", privatele date de mine altora (nu sunt pe ele) stau separat, sub tablou: le
    urmaresc, nu le lucrez.
  */
  const dateDeMine = useMemo(
    () => (aleMele ? toate.filter((t) => !esteAlMeu(t, eu?.id) && t.stare !== 'done') : []),
    [toate, aleMele, eu?.id],
  )
  const peTablou = useMemo(
    () => (aleMele ? toate.filter((t) => esteAlMeu(t, eu?.id) || t.stare === 'done') : toate),
    [toate, aleMele, eu?.id],
  )
  const numar = {
    urgente: peTablou.filter((t) => t.urgent && t.stare !== 'done').length,
    depasite: peTablou.filter(depasit).length,
    primite: peTablou.filter((t) => t.sursa != null).length,
    aleMele: peTablou.filter((t) => esteAlMeu(t, eu?.id) && t.stare !== 'done').length,
    private: peTablou.filter((t) => t.privat && t.stare !== 'done').length,
    email: peTablou.filter((t) => t.departamentPropus != null && t.stare !== 'done').length,
  }
  const vizibile = useMemo(() => {
    let l = peTablou
    if (filtru === 'urgente') l = l.filter((t) => t.urgent && t.stare !== 'done')
    if (filtru === 'depasite') l = l.filter(depasit)
    if (filtru === 'primite') l = l.filter((t) => t.sursa != null)
    if (filtru === 'ale-mele') l = l.filter((t) => esteAlMeu(t, eu?.id))
    if (filtru === 'private') l = l.filter((t) => t.privat)
    if (filtru === 'email') l = l.filter((t) => t.departamentPropus != null)
    const q = normalizeaza(cauta.trim())
    if (potriviri) l = l.filter((t) => potriviri.has(t.id))
    // Pana raspunde serverul (sau daca nu raspunde): titlul si clientul, ca inainte.
    else if (q)
      l = l.filter(
        (t) => normalizeaza(t.titlu).includes(q) || normalizeaza(t.client ?? '').includes(q),
      )
    return l
  }, [peTablou, filtru, cauta, potriviri, eu?.id])
  const deschise = useMemo(
    () =>
      ordoneazaPeTablou(
        vizibile.filter((t) => t.stare !== 'done'),
        ordini,
      ),
    [vizibile, ordini],
  )
  const semnGata = ordini.done === 'vechi' ? 1 : -1
  const terminate = vizibile
    .filter((t) => t.stare === 'done')
    .sort(
      (a, b) =>
        semnGata * (new Date(a.inchis ?? 0).getTime() - new Date(b.inchis ?? 0).getTime()) ||
        a.id - b.id,
    )

  const deschide = (id: number) => schimba({ view: String(id) })
  const muta = async (t: CardTask, spre: StareTablou) => {
    const a = actiuneSpre(t.stare, spre)
    if (!a) return
    /*
      Un card terminat tras inapoi se redeschide — cu aceeasi intrebare ca in panou. Pe tablou
      e la o scapare de mouse distanta, iar redeschiderea sterge nota de final, si la o noua
      inchidere cel care a cerut e anuntat inca o data.
    */
    if (
      a === 'redeschide' &&
      !(await confirma({
        titlu: `Redeschizi „${t.titlu}”?`,
        mesaj: 'Revine în „În lucru”, la cine l-a terminat.',
        buton: 'Redeschide',
      }))
    )
      return
    if (a === 'gata') return gata(t)
    // „Așteaptă" dintr-o miscare, fara fereastra „pe cine aștepți" (Madalin, 19.10.2026).
    actiune.mutate({ id: t.id, actiune: a })
  }
  /** Ca in panou: intai nota de final, optionala. Renunt = cardul ramane unde era. */
  const gata = async (t: CardTask) => {
    const nota = await cere({ ...FEREASTRA_GATA, titlu: `Gata: „${t.titlu}”` })
    if (nota === null) return
    actiune.mutate({ id: t.id, actiune: 'gata', motiv: nota || null })
  }

  // Adaugarea rapida, in „De preluat": doar pe tabloul unui departament in care omul poate crea.
  const depRapid: DepartamentSlug | null =
    poateScrie &&
    scop !== 'toate' &&
    scop !== 'trimise' &&
    scop !== 'ale-mele' &&
    (admin || scop === alMeu)
      ? scop
      : null
  // In „Ale mele" nu exista „De preluat": tot ce e acolo e deja al tau. Textele vorbesc cu tine.
  const coloane = [...(aleMele ? COLOANE_ALE_MELE : COLOANE), COLOANA_GATA]

  const vederi: [Vedere, string][] = [
    ['tablou', 'Tablou'],
    ...(aleMele ? ([['calendar', 'Calendar']] as [Vedere, string][]) : []),
    ['activitate', 'Activitate'],
    ['arhiva', 'Arhivă'],
    ...(admin && !aleMele ? ([['ansamblu', 'Ansamblu']] as [Vedere, string][]) : []),
  ]

  return (
    <div className="grid gap-4 p-4 pb-16 [&>*]:min-w-0">
      <AntetPagina
        titlu="Task-uri"
        descriere={
          aleMele
            ? 'Ce ai tu de făcut, din orice departament, și privatele tale. Doar tu vezi acest card.'
            : admin
              ? 'Munca fiecărui departament, de la ce a intrat până la ce s-a terminat.'
              : 'Munca departamentului tău, de la ce a intrat până la ce s-a terminat. Ce trimiteți altora apare la „Trimise de noi”.'
        }
        explicatie="Un task merge la un departament (îl preia cine are timp) sau direct la unul ori mai mulți oameni; oricare dintre ei îl mută și îl închide. Unul privat îl văd doar cei de pe el, cine l-a dat și cine urmărește privatele. Scurtături: N task nou, / caută, Esc închide."
        actiuni={
          <>
            <Link
              to="/task-uri/recurente"
              className="inline-flex items-center gap-1.5 rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-2 text-corp text-b4l-text hover:bg-b4l-bg"
            >
              <Repeat size={15} aria-hidden="true" /> Recurente
            </Link>
            {poateScrie && (
              <Button onClick={() => setNou(true)}>
                <Plus size={15} aria-hidden="true" /> Task nou
              </Button>
            )}
          </>
        }
      />

      <CarduriDepartamente
        date={departamente.data}
        scop={scop}
        admin={admin}
        laAlegere={(s) => schimba({ dep: s })}
      />

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5">
        <div
          className="inline-flex gap-0.5 rounded-caseta bg-b4l-bg p-0.5"
          role="group"
          aria-label="Vedere"
        >
          {vederi.map(([k, e]) => (
            <button
              key={k}
              type="button"
              aria-pressed={vedere === k}
              onClick={() => schimba({ vedere: k === 'tablou' ? null : k })}
              className={`rounded-md px-3 py-1.5 text-corp font-medium ${
                vedere === k
                  ? 'bg-b4l-surface text-b4l-text shadow-sm'
                  : 'text-b4l-text-muted hover:text-b4l-text'
              }`}
            >
              {e}
            </button>
          ))}
        </div>
        {(vedere === 'tablou' || vedere === 'calendar') && (
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip activ={filtru === 'toate'} onClick={() => setFiltru('toate')}>
              Toate
            </Chip>
            <Chip
              activ={filtru === 'urgente'}
              onClick={() => setFiltru('urgente')}
              n={numar.urgente}
            >
              Urgente
            </Chip>
            <Chip
              activ={filtru === 'depasite'}
              onClick={() => setFiltru('depasite')}
              n={numar.depasite}
            >
              Depășite
            </Chip>
            {(aleMele || numar.private > 0) && (
              <Chip
                activ={filtru === 'private'}
                onClick={() => setFiltru('private')}
                n={numar.private}
              >
                Private
              </Chip>
            )}
            {aleMele && numar.email > 0 && (
              <Chip activ={filtru === 'email'} onClick={() => setFiltru('email')} n={numar.email}>
                Din email
              </Chip>
            )}
            {!trimise && !aleMele && (
              <Chip
                activ={filtru === 'primite'}
                onClick={() => setFiltru('primite')}
                n={numar.primite}
              >
                Cereri primite
              </Chip>
            )}
            {!trimise && !admin && !aleMele && (
              <Chip
                activ={filtru === 'ale-mele'}
                onClick={() => setFiltru('ale-mele')}
                n={numar.aleMele}
              >
                Preluate de mine
              </Chip>
            )}
            <label className="relative">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-b4l-text-dim"
                aria-hidden="true"
              />
              <input
                ref={campCauta}
                value={cauta}
                onChange={(e) => setCauta(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && (setCauta(''), e.currentTarget.blur())}
                placeholder="Caută: titlu, detalii, oameni, #nr  /"
                aria-label="Caută pe tablou — în titlu, detalii, responsabili, cine l-a creat, numărul task-ului, comentarii"
                title="Fără diacritice; mai multe cuvinte = toate; „între ghilimele” = text exact; #2445 = task-ul 2445"
                className="w-64 max-sm:w-full rounded-caseta border border-b4l-border bg-b4l-surface py-1.5 pl-7 pr-2.5 text-corp text-b4l-text"
              />
            </label>
          </div>
        )}
      </div>

      {vedere === 'tablou' && (
        <StareLista interogare={tablou} gol={false} mesajGol="">
          <div className="flex flex-wrap items-center justify-between gap-x-3.5 gap-y-2">
            <span className="text-mic text-b4l-text-dim">
              {potriviri ? (
                <>
                  {vizibile.length === 0
                    ? 'Nimic găsit pe tablou. Încearcă alt cuvânt, sau caută în Arhivă.'
                    : `${vizibile.length} ${vizibile.length === 1 ? 'task găsit' : 'task-uri găsite'}.`}
                  {cautare.data?.maiMulte && ' Sunt mai multe — scrie încă un cuvânt.'}
                </>
              ) : trimise ? (
                'Cererile voastre, așa cum le vede celălalt departament. Le poți comenta, nu muta.'
              ) : (
                'Trage un card în altă coloană, sau folosește ⋯ „Mută în”.'
              )}
            </span>
          </div>
          <Tablou<CardTask, StareTablou>
            coloane={coloane}
            elemente={[...deschise, ...terminate]}
            cheie={(t) => t.id}
            coloana={(t) => coloanaPeTablou(t.stare)}
            tinte={tinteCard}
            laMutare={muta}
            eticheta={(t) => t.titlu}
            randeaza={(t) => (
              <CardFata
                t={t}
                cuDepartament={cuDepartament}
                trimis={trimise}
                laDeschidere={() => deschide(t.id)}
                laPasUrmator={() => {
                  const p = PAS_URMATOR[t.stare]
                  if (p?.actiune === 'gata') void gata(t)
                  else if (p) actiune.mutate({ id: t.id, actiune: p.actiune })
                }}
                laResponsabil={t.poateOameni ? () => setDeDat(t) : undefined}
                laArhivare={t.actiuni.includes('arhiveaza') ? () => arhiveaza(t) : undefined}
                laStergere={admin ? () => sterge(t) : undefined}
                // Pe tabloul Contabilitatii toate sunt de bani: acolo rosul n-ar mai deosebi nimic.
                // Cele finalizate raman verzi, si cele de bani (Madalin, 27.10.2026).
                financiar={scop !== 'contabilitate' && t.stare !== 'done' && esteFinanciar(t)}
                motive={potriviri?.get(t.id)}
              />
            )}
            inTitlu={(c, nr) =>
              nr > 1 ? (
                <ButonOrdine
                  ordine={ordineaLui(c.id)}
                  laSchimbare={schimbaOrdinea(c.id)}
                  ce="puse în coloană"
                />
              ) : null
            }
            inCap={(c, nr) =>
              c.id === 'in_progress' && oameniInDep > 0 && nr > oameniInDep * 2 ? (
                <span className="mt-1 rounded-md bg-b4l-amber-light px-2 py-1 text-eticheta text-b4l-amber-dark">
                  {nr} în lucru pentru {oameniInDep} {oameniInDep === 1 ? 'om' : 'oameni'} — poate
                  merită terminat ceva înainte de a începe altceva.
                </span>
              ) : null
            }
            inainteDeCarduri={(c) =>
              c.id === 'done' ? (
                <button
                  type="button"
                  onClick={() => schimba({ vedere: 'arhiva' })}
                  className="justify-self-start px-1.5 text-mic text-b4l-blue hover:underline"
                >
                  Toată arhiva →
                </button>
              ) : c.id === 'de_preluat' && depRapid ? (
                <AdaugaRapid departament={depRapid} />
              ) : c.id === 'in_progress' && aleMele && poateScrie && eu ? (
                <AdaugaRapid privatPentru={eu.id} />
              ) : null
            }
          />
          {aleMele && dateDeMine.length > 0 && (
            <DateDeMine taskuri={dateDeMine} laDeschidere={deschide} />
          )}
        </StareLista>
      )}
      {vedere === 'calendar' && (
        <StareLista interogare={tablou} gol={false} mesajGol="">
          <VedereCalendar
            taskuri={vizibile.filter((t) => esteAlMeu(t, eu?.id))}
            laDeschidere={deschide}
            laTermen={(t, termen) =>
              api
                .patch(`/tasks/${t.id}`, { termen })
                .then(() => {
                  toast.success(`„${t.titlu}” are termen ${ziScurt(termen)}`)
                  reimprospateaza()
                })
                .catch((e: unknown) => toast.error(mesajEroare(e, 'Nu s-a putut pune termenul.')))
            }
          />
        </StareLista>
      )}
      {vedere === 'activitate' && <VedereActivitate scop={scop} laDeschidere={deschide} />}
      {vedere === 'arhiva' && <VedereArhiva scop={scop} laDeschidere={deschide} />}
      {vedere === 'ansamblu' && admin && (
        <VedereAnsamblu scop={scop} laAlegere={(d) => schimba({ dep: d, vedere: null })} />
      )}

      {deschisId && (
        <PanouTask key={deschisId} taskId={deschisId} laInchidere={() => schimba({ view: null })} />
      )}
      {nou && (
        <FormularTask
          alMeu={alMeu}
          departamentInitial={
            scop !== 'toate' && scop !== 'trimise' && scop !== 'ale-mele' ? scop : alMeu
          }
          privatInitial={aleMele}
          laInchidere={() => setNou(false)}
        />
      )}
      {deDat && (
        <AlegeOameni
          task={{
            id: deDat.id,
            titlu: deDat.titlu,
            departament: deDat.departament,
            privat: deDat.privat,
            responsabili: deDat.responsabili,
            poateScoateAltii: deDat.poateScoateAltii,
          }}
          laInchidere={() => setDeDat(null)}
        />
      )}
    </div>
  )
}

function Chip({
  activ,
  onClick,
  n,
  children,
}: {
  activ: boolean
  onClick: () => void
  n?: number
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={activ}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-mic ${
        activ
          ? 'border-b4l-blue bg-b4l-blue-light font-semibold text-b4l-blue-dark'
          : 'border-b4l-border bg-b4l-surface text-b4l-text hover:border-b4l-blue'
      }`}
    >
      {children}
      {n != null && <span className={`tabular-nums ${activ ? '' : 'text-b4l-text-dim'}`}>{n}</span>}
    </button>
  )
}

/**
 * Titlul si Enter. Pe tabloul unui departament: task nou la „De preluat". In „Ale mele": un task
 * PRIVAT, al tau (19.10.2026: „fiecare sa-si poata crea un task privat"). Restul (detalii,
 * termen, alti oameni) se completeaza dupa, din panou.
 */
function AdaugaRapid({
  departament,
  privatPentru,
}: {
  departament?: DepartamentSlug
  privatPentru?: number
}) {
  const [titlu, setTitlu] = useState('')
  const creeaza = useCreeaza()
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!titlu.trim() || creeaza.isPending) return
        creeaza.mutate(
          privatPentru
            ? { privat: true, oameni: [privatPentru], titlu: titlu.trim() }
            : { departament, titlu: titlu.trim() },
          { onSuccess: () => setTitlu('') },
        )
      }}
    >
      <input
        value={titlu}
        onChange={(e) => setTitlu(e.target.value)}
        maxLength={200}
        disabled={creeaza.isPending}
        placeholder={
          privatPentru ? '+ Task privat, doar al tău (Enter)' : '+ Adaugă rapid aici (Enter)'
        }
        aria-label={
          privatPentru
            ? 'Adaugă rapid un task privat, doar al tău'
            : `Adaugă rapid un task la ${numeDepartament(departament!)}`
        }
        className="w-full rounded-caseta border border-dashed border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-corp text-b4l-text placeholder:text-b4l-text-dim focus:border-solid focus:border-b4l-blue"
      />
    </form>
  )
}

/** Cardurile de sus: fiecare departament vizibil (selector), plus „Trimise de noi". */
function CarduriDepartamente({
  date,
  scop,
  admin,
  laAlegere,
}: {
  date: ReturnType<typeof useDepartamente>['data']
  scop: Scop
  admin: boolean
  laAlegere: (s: Scop) => void
}) {
  if (!date) return <div className="h-[118px]" aria-hidden="true" />
  const total: SumarDepartament = date.departamente.reduce(
    (s, d) => ({
      dePreluat: s.dePreluat + d.dePreluat,
      inLucru: s.inLucru + d.inLucru,
      asteapta: s.asteapta + d.asteapta,
      deschise: s.deschise + d.deschise,
      depasite: s.depasite + d.depasite,
    }),
    { dePreluat: 0, inLucru: 0, asteapta: 0, deschise: 0, depasite: 0 },
  )
  return (
    <section
      className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(178px,1fr))]"
      aria-label="Departamente"
    >
      <CardDep
        nume="Ale mele"
        sub={
          date.aleMele.private
            ? `${date.aleMele.private} ${date.aleMele.private === 1 ? 'privat' : 'private'} · doar tu le vezi`
            : 'doar tu vezi acest card'
        }
        s={{ ...date.aleMele, dePreluat: 0 }}
        activ={scop === 'ale-mele'}
        inel="border-b4l-blue ring-b4l-blue"
        alMeu
        onClick={() => laAlegere('ale-mele')}
      />
      {admin && (
        <CardDep
          nume="Toate departamentele"
          s={total}
          activ={scop === 'toate'}
          inel="border-b4l-text-muted ring-b4l-text-muted"
          onClick={() => laAlegere('toate')}
        />
      )}
      {date.departamente.map((d) => (
        <CardDep
          key={d.slug}
          nume={d.nume}
          sub={d.slug === date.departamentulMeu ? 'departamentul tău' : undefined}
          s={d}
          activ={scop === d.slug}
          inel={CULOARE_DEPARTAMENT[d.slug].inel}
          onClick={() => laAlegere(d.slug)}
        />
      ))}
      {date.trimise && (
        <CardDep
          nume="Trimise de noi"
          sub="cereri la alte departamente"
          s={date.trimise}
          activ={scop === 'trimise'}
          inel="border-b4l-purple ring-b4l-purple"
          trimise
          onClick={() => laAlegere('trimise')}
        />
      )}
    </section>
  )
}

function CardDep({
  nume,
  sub,
  s,
  activ,
  inel,
  trimise,
  alMeu,
  onClick,
}: {
  nume: string
  sub?: string
  s: SumarDepartament
  activ: boolean
  inel: string
  trimise?: boolean
  /** „Ale mele": fara „de preluat" — tot ce e acolo e deja al tau. */
  alMeu?: boolean
  onClick: () => void
}) {
  const segmente = [
    [s.dePreluat, 'bg-b4l-amber'],
    [s.inLucru, 'bg-b4l-blue'],
    [s.asteapta, 'bg-b4l-purple'],
  ] as const
  return (
    <button
      type="button"
      aria-pressed={activ}
      onClick={onClick}
      className={`grid content-start gap-2 rounded-caseta border bg-b4l-surface px-3.5 py-3 text-left transition-shadow hover:shadow-ridicat ${
        trimise ? 'border-dashed' : ''
      } ${activ ? `${inel} ring-1` : 'border-b4l-border'}`}
    >
      <span className="grid gap-px">
        <span className="text-corp font-semibold text-b4l-text">{nume}</span>
        {sub && <span className="text-eticheta font-medium text-b4l-text-dim">{sub}</span>}
      </span>
      <span className="flex items-baseline gap-1.5 text-mic text-b4l-text-muted">
        <b className="text-[24px] font-semibold leading-7 tabular-nums tracking-tight text-b4l-text">
          {s.deschise}
        </b>
        {trimise ? 'în curs la alții' : 'deschise'}
      </span>
      <span className="flex h-1 gap-0.5 overflow-hidden rounded-sm bg-b4l-bg" aria-hidden="true">
        {segmente.map(([n, c], i) => (
          <i key={i} className={`block min-w-0 ${c}`} style={{ flex: n }} />
        ))}
      </span>
      <span className="flex flex-wrap gap-x-2.5 gap-y-1 text-eticheta tabular-nums">
        {alMeu ? (
          <span className="text-b4l-text-dim">
            {s.inLucru} în lucru{s.asteapta ? ` · ${s.asteapta} așteaptă` : ''}
          </span>
        ) : s.dePreluat ? (
          <span className="font-semibold text-b4l-amber-dark">
            {s.dePreluat}{' '}
            {trimise ? (s.dePreluat === 1 ? 'nepreluată încă' : 'nepreluate încă') : 'de preluat'}
          </span>
        ) : (
          <span className="text-b4l-text-dim">
            {trimise
              ? s.deschise
                ? 'toate preluate'
                : 'nimic în curs la alții'
              : 'nimic de preluat'}
          </span>
        )}
        {s.depasite > 0 && (
          <span className="font-semibold text-b4l-red-dark">
            {s.depasite} {s.depasite === 1 ? 'depășit' : 'depășite'}
          </span>
        )}
      </span>
    </button>
  )
}

/**
 * „Ale mele": privatele date de tine altora — le vezi ca sa le urmaresti, nu ca sa le lucrezi
 * (macheta 19.10.2026). Sub tablou: la cine e, in ce stare, de cand.
 */
function DateDeMine({
  taskuri,
  laDeschidere,
}: {
  taskuri: CardTask[]
  laDeschidere: (id: number) => void
}) {
  return (
    <section
      className="grid gap-1.5 rounded-caseta border border-dashed border-b4l-border bg-b4l-surface p-3"
      aria-label="Date de tine altora"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 text-corp text-b4l-text">
        <Lock size={13} className="self-center text-b4l-purple-dark" aria-hidden="true" />
        <b className="font-semibold">Date de tine altora</b>
        <span className="tabular-nums">{taskuri.length}</span>
        <span className="text-mic text-b4l-text-dim">
          · private; le vezi până se închid și se întorc la tine dacă rămân fără nimeni
        </span>
      </div>
      <div className="grid gap-x-3 gap-y-0.5 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
        {taskuri.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => laDeschidere(t.id)}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-md px-1 py-1.5 text-left text-corp text-b4l-text hover:bg-b4l-bg-soft"
          >
            <span className="truncate">{t.titlu}</span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-mic text-b4l-text-dim">
              {COLOANE.find((c) => c.id === coloanaPeTablou(t.stare))?.nume}
              <Avatare oameni={t.responsabili} />
            </span>
          </button>
        ))}
      </div>
    </section>
  )
}
